import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useUIContext } from './UIContext';
import logger from '../utils/logger';

const NavigationContext = createContext();

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const { page, setPage, goBack: uiGoBack, pageHistory } = useUIContext();

  // Define navigation hierarchy and allowed back navigation
  const navigationMap = useMemo(() => ({
    'dashboard': { canGoBack: false, parent: null },
    'learning_path': { canGoBack: true, parent: 'dashboard' },
    'practice': { canGoBack: true, parent: 'dashboard' },
    'game_zone': { canGoBack: true, parent: 'dashboard' },
    'sticker_book': { canGoBack: true, parent: null }, // Remove hardcoded parent to allow proper back navigation
    'profile': { canGoBack: true, parent: 'dashboard' },
    'settings': { canGoBack: true, parent: 'dashboard' },
    'notifications': { canGoBack: true, parent: null }, // Remove hardcoded parent to allow proper back navigation
    'onboarding': { canGoBack: false, parent: null },
    'loading': { canGoBack: false, parent: null },
    'parental_auth': { canGoBack: true, parent: 'dashboard' },
    'storybooks': { canGoBack: true, parent: 'dashboard' },
    'lesson': { canGoBack: true, parent: 'learning_path' },
    'quiz': { canGoBack: true, parent: 'learning_path' },
    'activity': { canGoBack: true, parent: 'learning_path' }
  }), []);

  // Navigate to a new page and update history
  const navigateTo = useCallback((newPage, addToHistory = true) => {
    logger.info('NavigationContext', 'Info', newPage, 'addToHistory:', addToHistory);
    setPage(newPage);
    
    // Update browser history to support native back gesture on mobile
    // This allows Android/iOS back gestures to work with in-app navigation
    if (addToHistory && typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      const newUrl = `${window.location.origin}${window.location.pathname}#${newPage}`;
      
      // Only push if the URL is actually changing
      if (currentUrl !== newUrl) {
        logger.info('NavigationContext', 'Info', newPage);
        window.history.pushState({ page: newPage }, '', newUrl);
      }
    }
  }, [setPage]);

  // Navigate back to previous page
  const navigateBack = useCallback(() => {
    logger.info('NavigateBack', 'Info', page, 'History:', pageHistory);
    logger.info('NavigateBack', 'Info', pageHistory?.length);
    logger.info('NavigateBack', 'Info', pageHistory?.[pageHistory.length - 1]);
    
    const currentPageInfo = navigationMap[page];
    
    if (!currentPageInfo || !currentPageInfo.canGoBack) {
      logger.info('NavigateBack', 'Info', page);
      return false;
    }

    // Check if we have a valid history to go back to
    const validPages = [
      'dashboard', 'learning_path', 'practice', 'game_zone', 'sticker_book', 
      'storybook', 'profile', 'settings', 'parental_auth',
      'lesson', 'quiz', 'quiz_results', 'profile_picker', 'add_new_profile',
      'forgot_password', 'support_page', 'subscription_page', 'post_subscribe_onboarding',
      'trial_complete_transition', 'notifications', 'unsubscribe', 'analytics_dashboard',
      'modal_test', 'categorySort_activity', 'colorMatch_activity', 'listenFind_activity',
      'numberMatch_activity', 'sentenceScramble_activity', 'whatAmI_activity',
      'simpleConversation_activity'
    ];

    // Check if the last page in history is valid
    const lastPageInHistory = pageHistory?.[pageHistory.length - 1];
    const isHistoryValid = pageHistory && pageHistory.length > 1 && validPages.includes(lastPageInHistory);
    
    if (!isHistoryValid) {
      logger.info('NavigateBack', 'Info');
      // Use the parent field as fallback
      if (currentPageInfo.parent) {
        logger.info('NavigateBack', 'Info', currentPageInfo.parent);
        navigateTo(currentPageInfo.parent, false);
        return true;
      } else {
        logger.info('NavigateBack', 'Info');
        // Smart fallback based on current page
        let fallbackPage = 'learning_path';
        if (page === 'lesson' || page === 'quiz' || page === 'quiz_results') {
          fallbackPage = 'learning_path';
        } else if (page === 'practice') {
          fallbackPage = 'learning_path';
        } else if (page === 'game_zone' || page === 'sticker_book' || page === 'storybook') {
          fallbackPage = 'dashboard';
        }
        logger.info('NavigateBack', 'Info', fallbackPage);
        navigateTo(fallbackPage, false);
        return true;
      }
    }

    // Use UIContext's goBack function which properly manages pageHistory
    logger.info('NavigateBack', 'Info');
    uiGoBack();
    logger.info('NavigateBack', 'Info');
    
    return true;
  }, [page, pageHistory, navigationMap, uiGoBack, navigateTo]);

  // Handle browser back/forward navigation (including native swipe gestures)
  useEffect(() => {
    const handlePopState = (event) => {
      logger.info('NavigationContext', 'Info', event.state);
      
      // If there's a page in the state, navigate to it
      if (event.state && event.state.page) {
        logger.info('NavigationContext', 'Info', event.state.page);
        setPage(event.state.page);
      } else {
        // If no state, try to parse from hash
        const hash = window.location.hash.substring(1); // Remove #
        if (hash && hash !== page) {
          logger.info('NavigationContext', 'Info', hash);
          setPage(hash);
        } else {
          // Fallback: use our internal navigation
          logger.info('NavigationContext', 'Info');
          navigateBack();
        }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    logger.info('NavigationContext', 'Info');
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [page, setPage, navigateBack]);

  // Prevent accidental app closure on mobile
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Only show warning if user has made progress
      const hasProgress = localStorage.getItem('hasSeenWelcomeModal') === 'true';
      if (hasProgress) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const value = {
    navigationHistory: pageHistory, // Use pageHistory from UIContext
    navigateTo,
    navigateBack,
    canGoBack: navigationMap[page]?.canGoBack || false
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
